/**
 *  Announcement Bar - Salesforce Data Cloud (360) personalisation
 *
 *  This script attaches to the existing `.announcement-bar` markup (no
 *  custom element wrapper is used / no markup ownership). On load it scans
 *  for `.announcement-bar--item[data-personalisation-id]` elements inside
 *  every announcement bar on the page, batches one `Personalization.fetch`
 *  call per bar (de-duped by point name - the marquee layout duplicates
 *  each item up to 15 times), then swaps the inner text inside
 *  `.announcement-bar--item__text` with the personalised text body
 *  returned by Salesforce Data Cloud (360).
 *
 *  Attribute key: Content Zones for this Personalisation Point use the
 *  `Content` attribute (e.g. `attributes.Content`). We fall back to
 *  `Text`/`HTML` so the same JS works against legacy templates.
 *
 *  innerHTML (not textContent) is used because the announcement-bar text
 *  textarea in this theme legitimately accepts inline HTML (links, spans
 *  with styles, etc.) and personalised content honours the same
 *  authoring conventions.
 */
const announcementBarIsShiels = () => window.STORE_CONTEXT?.isShiels === true;

/**
 *  Salesforce Data Cloud (360) measurement traces (uni2 vocabulary):
 *    - announcementBar_decision        (every personalisation evaluation, per point)
 *    - announcementBar_view            (impression once the item is visible in viewport)
 *    - announcementBar_click           (anchor / button click inside a personalised item)
 *    - announcementBar_control         (posture = control)
 *    - announcementBar_suppressed      (posture = suppressed)
 *    - announcementBar_hidden_no_match (posture = no_match / hidden / no payload returned)
 *
 *  The marquee layout duplicates each item up to 15 times; trace emission is
 *  de-duplicated per Personalisation Point name so impression counts stay
 *  accurate. View is observed on the first canonical item only.
 *
 *  Final payload shape must be confirmed with Performance Lead before go-live.
 */
const announcementBarEmitTrace = (eventType, context = {}) => {
  if (!announcementBarIsShiels()) return;
  if (
    typeof window === "undefined" ||
    !window.SalesforceInteractions ||
    typeof window.SalesforceInteractions.sendEvent !== "function"
  ) {
    return;
  }
  const {
    personalisationId,
    contentId,
    decisionId,
    personalizationPointId,
    personalizationPointName,
    posture,
    attributes,
    source,
    href,
  } = context;
  try {
    window.SalesforceInteractions.sendEvent({
      interaction: {
        name: `announcementBar_${eventType}`,
        eventType: "userEngagement",
        engagementType: eventType,
        source: source || "announcement_bar",
        personalization: contentId ? { id: contentId, contentId: contentId } : undefined,
        personalizationPointName,
        personalizationPointId,
        contentId,
        decisionId,
        posture,
        href,
        attributes: attributes || {},
      },
    });
    console.log("[announcement-bar] Trace", eventType, context);
  } catch (e) {
    console.warn("[announcement-bar] Trace failed", eventType, e);
  }
};

const announcementBarResolvePosture = (attributes) => {
  if (!attributes) return "active";
  const raw =
    attributes.Posture ||
    attributes.posture ||
    attributes.Variant ||
    attributes.Decision;
  if (!raw) return "active";
  const v = String(raw).toLowerCase().replace(/[\s\-]+/g, "_");
  if (v === "active" || v === "variant" || v === "show") return "active";
  if (v === "control" || v === "holdout") return "control";
  if (v === "suppressed" || v === "suppress") return "suppressed";
  if (v === "no_match" || v === "nomatch" || v === "hidden" || v === "hide") {
    return "no_match";
  }
  return "active";
};

(function () {
  if (window.__announcementBarPersonalisationInit) return;
  window.__announcementBarPersonalisationInit = true;

  const start = () => {
    if (!announcementBarIsShiels()) return;
    console.log("[announcement-bar] script loaded");
    const bars = Array.from(document.querySelectorAll(".announcement-bar"));
    console.log(`[announcement-bar] init - found ${bars.length} bar(s) on the page`);
    bars.forEach(initBar);

    // Also handle theme editor re-renders so changing settings in the
    // customizer doesn't leave the personalisation in a broken state.
    document.addEventListener("shopify:section:load", (event) => {
      const newBars = event.target ? event.target.querySelectorAll(".announcement-bar") : [];
      newBars.forEach(initBar);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  function initBar(bar) {
    if (!announcementBarIsShiels()) return;
    if (!bar || bar.dataset.personalisationInit === "true") return;
    bar.dataset.personalisationInit = "true";

    const allItems = Array.from(bar.querySelectorAll(".announcement-bar--item"));
    const items = Array.from(bar.querySelectorAll(".announcement-bar--item[data-personalisation-id]"));
    console.log(
      `[announcement-bar] bar inspected - ${allItems.length} item(s) total, ${items.length} with a personalisation ID`
    );
    if (items.length === 0) {
      console.log(
        "[announcement-bar] no items with data-personalisation-id on this bar - nothing to fetch. " +
          "Add a Personalisation ID to at least one Text block in the theme editor."
      );
      return;
    }

    // De-duplicate by personalisation point name so we issue one SDK call.
    // The same point may appear on multiple DOM nodes (e.g. marquee clones).
    const itemsByPointName = new Map();
    items.forEach((item) => {
      const id = item.dataset.personalisationId;
      if (!id) return;
      if (!itemsByPointName.has(id)) itemsByPointName.set(id, []);
      itemsByPointName.get(id).push(item);
    });
    const pointNames = Array.from(itemsByPointName.keys());

    const clearSkeleton = (item) => {
      if (!item || !item.classList.contains("announcement-bar--item--personalising")) return;
      item.classList.remove("announcement-bar--item--personalising");
      const skeleton = item.querySelector(".announcement-bar--item-skeleton");
      if (skeleton) {
        setTimeout(() => skeleton.remove(), 400);
      }
    };

    // Safety net so we never leave the skeleton showing forever if the SDK
    // never becomes available (ad blockers, network failures, etc.).
    const SKELETON_TIMEOUT_MS = 6000;
    const skeletonTimeout = setTimeout(() => {
      items.forEach(clearSkeleton);
    }, SKELETON_TIMEOUT_MS);

    const MAX_WAIT_MS = 15000;
    const POLL_INTERVAL_MS = 100;
    const sdkStart = Date.now();

    const sdkReady = () =>
      typeof window !== "undefined" &&
      window.SalesforceInteractions &&
      window.SalesforceInteractions.Personalization &&
      typeof window.SalesforceInteractions.Personalization.fetch === "function";

    const resolveSdk = () =>
      new Promise((resolve, reject) => {
        if (sdkReady()) return resolve(window.SalesforceInteractions);
        const interval = setInterval(() => {
          if (sdkReady()) {
            clearInterval(interval);
            resolve(window.SalesforceInteractions);
          } else if (Date.now() - sdkStart > MAX_WAIT_MS) {
            clearInterval(interval);
            reject(new Error("Salesforce Data Cloud SDK / Personalization.fetch not available"));
          }
        }, POLL_INTERVAL_MS);
      });

    const applyToItem = (item, personalization, traceContext) => {
      const attributes = (personalization && personalization.attributes) || {};
      const contentId = traceContext.contentId;
      const decisionId = traceContext.decisionId;
      const personalizationPointId = traceContext.personalizationPointId;
      const personalizationPointName = traceContext.personalizationPointName;

      if (contentId) item.dataset.personalisationContentId = contentId;
      if (decisionId) item.dataset.personalisationDecisionId = decisionId;
      if (personalizationPointId) item.dataset.personalisationPointId = personalizationPointId;
      if (personalizationPointName) item.dataset.personalisationPointName = personalizationPointName;
      item.dataset.personalisationPosture = traceContext.posture || "active";
      item._announcementBarTraceContext = traceContext;

      // Salesforce Data Cloud (360) Content Zones for this Personalisation
      // Point use the attribute key `Content` (capital C). We also accept
      // `Text` and a few lowercase variants so the same JS works for points
      // configured against legacy templates.
      const personalisedHtml =
        attributes.Content || attributes.Text || attributes.content || attributes.text || attributes.HTML || attributes.html;
      if (personalisedHtml) {
        const textEl = item.querySelector(".announcement-bar--item__text");
        if (textEl) textEl.innerHTML = personalisedHtml;
      } else {
        console.warn(
          "[announcement-bar] Personalisation returned no Content/Text attribute - leaving the merchant default in place",
          attributes
        );
      }

      clearSkeleton(item);

      item.dispatchEvent(
        new CustomEvent("announcement-bar:item:personalisation:ready", {
          bubbles: true,
          detail: {
            item,
            personalisationId: item.dataset.personalisationId,
            contentId,
            decisionId,
            personalizationPointId,
            personalizationPointName,
            posture: traceContext.posture,
            attributes,
            personalization,
          },
        })
      );
    };

    /**
     * Per-point handler: emits one decision trace, handles posture, applies
     * content + arms view/click traces only on the first (canonical) item to
     * keep impression counts accurate when the marquee duplicates the item.
     */
    const handlePersonalisation = (pointName, itemsForName, personalization, errorPosture) => {
      const attributes = (personalization && personalization.attributes) || {};
      const contentId =
        (personalization && personalization.personalizationId) ||
        attributes.contentId ||
        attributes.ContentId ||
        attributes.content_id ||
        null;
      const decisionId = personalization && personalization.decisionId;
      const personalizationPointId = personalization && personalization.personalizationPointId;
      const personalizationPointName =
        (personalization && personalization.personalizationPointName) || pointName;
      const posture = errorPosture || announcementBarResolvePosture(attributes);

      const traceContext = {
        personalisationId: pointName,
        contentId,
        decisionId,
        personalizationPointId,
        personalizationPointName,
        posture,
        attributes,
      };

      announcementBarEmitTrace("decision", traceContext);

      if (posture === "control") {
        announcementBarEmitTrace("control", traceContext);
        itemsForName.forEach(clearSkeleton);
        return;
      }
      if (posture === "suppressed") {
        announcementBarEmitTrace("suppressed", traceContext);
        itemsForName.forEach((item) => {
          item.classList.add("announcement-bar--item--suppressed");
          item.style.display = "none";
          clearSkeleton(item);
        });
        return;
      }
      if (posture === "no_match") {
        announcementBarEmitTrace("hidden_no_match", traceContext);
        itemsForName.forEach(clearSkeleton);
        return;
      }

      // active
      itemsForName.forEach((item) => applyToItem(item, personalization, traceContext));

      // View: observe only the first canonical item (marquee duplicates inflate count).
      const canonical = itemsForName[0];
      if (canonical && !canonical._announcementBarViewArmed) {
        canonical._announcementBarViewArmed = true;
        const fireView = (source) => {
          announcementBarEmitTrace("view", { ...traceContext, source: source || "viewport" });
        };
        if (typeof IntersectionObserver === "function") {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                if (canonical._announcementBarViewFired) return;
                canonical._announcementBarViewFired = true;
                fireView("viewport");
              });
            },
            { threshold: 0.5 }
          );
          observer.observe(canonical);
        } else {
          fireView("fallback");
        }
      }

      // Click: delegated on every duplicate item so any clone click counts.
      itemsForName.forEach((item) => {
        if (item._announcementBarClickArmed) return;
        item._announcementBarClickArmed = true;
        item.addEventListener("click", (e) => {
          const clickEl = e.target && e.target.closest("a, button, [role='button']");
          if (!clickEl || !item.contains(clickEl)) return;
          const href = clickEl.getAttribute("href") || undefined;
          announcementBarEmitTrace("click", {
            ...traceContext,
            source: "item_click",
            href,
          });
        });
      });
    };

    resolveSdk()
      .then((SalesforceInteractions) => {
        console.log("[announcement-bar] Fetching personalisation for items", pointNames);
        return SalesforceInteractions.Personalization.fetch(pointNames);
      })
      .then((response) => {
        clearTimeout(skeletonTimeout);
        const personalizations = (response && response.personalizations) || [];
        const byName = new Map();
        personalizations.forEach((p) => {
          if (p && p.personalizationPointName) byName.set(p.personalizationPointName, p);
        });

        itemsByPointName.forEach((itemsForName, name) => {
          const personalization = byName.get(name);
          if (!personalization) {
            console.warn(`[announcement-bar] No personalisation returned for "${name}"`);
            handlePersonalisation(name, itemsForName, null, "no_match");
            return;
          }
          console.log("[announcement-bar] Item personalisation result", {
            personalisationId: name,
            attributes: personalization.attributes,
            personalization,
          });
          handlePersonalisation(name, itemsForName, personalization);
        });
      })
      .catch((err) => {
        clearTimeout(skeletonTimeout);
        console.warn("[announcement-bar] Failed to fetch personalisations", pointNames, err);
        itemsByPointName.forEach((itemsForName, name) => {
          handlePersonalisation(name, itemsForName, null, "no_match");
          itemsForName.forEach((item) => {
            item.dispatchEvent(
              new CustomEvent("announcement-bar:item:personalisation:error", {
                bubbles: true,
                detail: { item, personalisationId: item.dataset.personalisationId, error: err },
              })
            );
          });
        });
      });
  }
})();
